import "./App.css";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { DeckGL } from "deck.gl";
import { GeoJsonLayer, TextLayer } from "@deck.gl/layers";
// Global Map yapıcısını gölgelememesi için takma adla alınır
import { Map as MapLibreMap } from "@vis.gl/react-maplibre";
import { WebMercatorViewport } from "@deck.gl/core";
import * as turf from "@turf/turf";
import "maplibre-gl/dist/maplibre-gl.css";
import { useTranslation } from "react-i18next";

/* Map movement bounds – Black Sea region only.
   Kuzey sınırı Odessa (46.55°K) ve Azak'ın güneyini kapsamalı; aksi halde
   kuzeydeki alanlar seçilse bile ilk pan/zoom'da kadraj dışına itiliyor. */
const MAP_BOUNDS = {
  minLng: 26.0,
  maxLng: 42.5,
  minLat: 40.0,
  maxLat: 47.5,
  minZoom: 5.0,
  maxZoom: 18,
};

/* Tüm Karadeniz havzası ve kıyıları – açılışta bu kapsam ekrana sığdırılır */
const BLACK_SEA_BOUNDS = [
  [27.0, 40.7], // güneybatı (Bulgar kıyısı / Boğaz)
  [42.0, 47.0], // kuzeydoğu (Batum / Odessa)
];

const FALLBACK_VIEW_STATE = {
  longitude: 34.5,
  latitude: 43.9,
  zoom: 5.8,
  pitch: 0,
  bearing: 0,
};

/* Pencere ölçüsüne göre tüm havzayı kadraja alan başlangıç görünümü */
function fitBlackSeaViewState() {
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  const height = typeof window !== "undefined" ? window.innerHeight : 0;
  if (!(width > 0 && height > 0)) return FALLBACK_VIEW_STATE;
  try {
    const vp = new WebMercatorViewport({
      ...FALLBACK_VIEW_STATE,
      width,
      height,
    }).fitBounds(BLACK_SEA_BOUNDS, { padding: 40 });
    return {
      ...FALLBACK_VIEW_STATE,
      longitude: vp.longitude,
      latitude: vp.latitude,
      zoom: Math.max(MAP_BOUNDS.minZoom, Math.min(MAP_BOUNDS.maxZoom, vp.zoom)),
    };
  } catch {
    return FALLBACK_VIEW_STATE;
  }
}

/* ── Default values for all filters ── */
const DEFAULTS = {
  sulfur: 0.1,
  phosphate: 0.03,
  nitrogen: 0.44,
  chlorophyll: 0.71,
  secchi: 11,
  hypoxia: 0.05,
  silica: 2.76,
  pollution: 0.1,
  temperature: 22.5,
  salinity: 18,
  regionPop: 0.3,
  industry: 0.1,
  shipWaste: 0.1,
  agriculture: 0.1,
  marina: 0.1,
  coastalBuild: 0.1,
  wastewater: 0.1,
  fishing: 0.1,
  aquaculture: 0.1,
  boat: 0.1,
  shoaling: 0.1,
  sedimentation: 0.1,
  stormImpact: 0.1,
  bathymetry: 10,
};

/* ── Zostera output metrics: coefficients & reference values ── */
const OUTPUT_COEFFICIENTS = {
  cover: 1.00,
  shoot: 1.25,
  rhizome: 1.15,
  meristem: 1.40,
};

const OUTPUT_REFERENCES = {
  cover:    { base: 55,  unit: "%" },
  shoot:    { base: 350, unit: "shoots/m²" },
  rhizome:  { base: 120, unit: "cm/m²" },
  meristem: { base: 45,  unit: "mers/m²" },
};

/* Metadata feature'ını dropdown girdisine indirger */
const toAreaEntry = (f) => ({
  id: String(f.properties?.id ?? ''),
  name: f.properties?.name || String(f.properties?.id ?? ''),
  country: f.properties?.country || null,
});

function computeOutputs(scale) {
  if (!Number.isFinite(scale)) return null;
  const delta = (scale - 1) * 100;
  const calc = (key) => {
    const d = delta * OUTPUT_COEFFICIENTS[key];
    const abs = OUTPUT_REFERENCES[key].base * (1 + d / 100);
    return { delta: d, abs, unit: OUTPUT_REFERENCES[key].unit };
  };
  return {
    cover:    calc("cover"),
    shoot:    calc("shoot"),
    rhizome:  calc("rhizome"),
    meristem: calc("meristem"),
  };
}


/* ── Slider color helper (blue→red based on negative impact) ── */
function getSliderColor(value, min, max, defaultVal, isPositive) {
  if (isPositive) {
    // For positive factors: decrease = negative
    const range = defaultVal - min;
    const ratio = range > 0 ? (defaultVal - value) / range : 0;
    const t = Math.max(0, Math.min(1, ratio));
    const hue = 210 - t * 210;
    return `hsl(${hue}, 75%, 50%)`;
  } else {
    // For negative factors: increase = negative
    const range = max - defaultVal;
    const ratio = range > 0 ? (value - defaultVal) / range : 0;
    const t = Math.max(0, Math.min(1, ratio));
    const hue = 210 - t * 210;
    return `hsl(${hue}, 75%, 50%)`;
  }
}

/* ══════════════════════════════════════════════════════════════ */
/*                         APP                                   */
/* ══════════════════════════════════════════════════════════════ */
function App() {
  // ── State ──
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null); // lightweight pseudo-FeatureCollection (metadata only, no polygon geometry)
  const [regionGeoData, setRegionGeoData] = useState(null); // [{ key, features }] — public/geojson bölge poligonları (tek veri kaynağı)
  const [dataLoaded, setDataLoaded] = useState(false); // worker tüm dosyaları bitirdi mi
  const [selectedFeatureGeo, setSelectedFeatureGeo] = useState(null); // full GeoJSON loaded on demand
  const [geoLoading, setGeoLoading] = useState(false);
  const [selectedMask, setSelectedMask] = useState(null); // seçili alanın hassas su maskesi
  const [maskLoading, setMaskLoading] = useState(false);  // maske inerken tahmin çizilmez
  const [viewState, setViewState] = useState(fitBlackSeaViewState);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [horizon, setHorizon] = useState('N');
  const [loadingState, setLoadingState] = useState('init'); // 'init' | 'ready'
  const [loadProgress, setLoadProgress] = useState(0); // 0 to 100

  // Draft filter state (user edits here, not applied until horizon click)
  const [filters, setFilters] = useState({ ...DEFAULTS });
  // Applied filter state (used for prediction calculations)
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULTS });

  // Modal state
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [transplantModalOpen, setTransplantModalOpen] = useState(false);

  // Active areas for transplantation feature
  const [activeAreaIds, setActiveAreaIds] = useState([]);
  const [transplantedAreaIds, setTransplantedAreaIds] = useState([]);

  // Her alan için filtre hafızası: { [areaId]: filtersObject }
  const filtersPerArea = useRef({});
  // Güncel filtrelerin ref kopyası — handleSelectFeature'ın `filters`'a bağımlı olmasını
  // (ve her slider hareketinde layers'ın yeniden kurulmasını) önler.
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // id → bölge feature'ı (worker ile yüklenen geojson geometrisi)
  const featuresById = useRef(new Map());

  // id → alana özel hassas su maskesi; seçim anında indirilir, bellekte tutulur
  const masksById = useRef(new Map());

  // ── Açılışta hafif metadata yükle; bölge geometrileri worker ile gelir ──
  useEffect(() => {
    // 1. Hafif metadata yükle (~2KB) — geometri yok, sadece özellikler + centroid + bbox
    fetch(process.env.PUBLIC_URL + '/features_meta.json')
      .then(r => r.json())
      .then(meta => {
        // Pseudo FeatureCollection oluştur (Point geometrili, geri kalan kod uyumlu kalsın)
        const pseudoFeatures = meta.features.map(f => ({
          type: "Feature",
          properties: {
            id: f.id,
            name: f.name,
            country: f.country,
            IsSatelliteData: f.IsSatelliteData,
            _centroid: f.centroid,
            _bbox: f.bbox,
            _hasMask: f.hasMask,
          },
          geometry: { type: "Point", coordinates: f.centroid },
        }));

        setData({ type: "FeatureCollection", features: pseudoFeatures });

        // Başlangıçta her ülkeden bir alan aktif olsun (metadata sırasındaki ilki)
        const seenCountries = new Set();
        const initialIds = pseudoFeatures
          .filter(f => {
            const c = f.properties.country || 'XX';
            if (seenCountries.has(c)) return false;
            seenCountries.add(c);
            return true;
          })
          .map(f => f.properties.id);
        setActiveAreaIds(initialIds);
        setLoadingState('ready');
        console.log(`Features metadata loaded: ${pseudoFeatures.length} features`);
      })
      .catch(e => {
        console.error("Failed to load features metadata:", e);
        setLoadingState('ready');
      });

    // 2. Bölge geometrilerini worker ile yükle (IndexedDB önbellekli).
    //    Her dosya kendi katmanı olur: yeni dosya gelince önceden işlenmiş
    //    geometriler yeniden üçgenlenmez.
    const worker = new Worker(process.env.PUBLIC_URL + '/data-worker.js');

    worker.onmessage = (e) => {
      const { type, file, data: fileData, progress, failed, message } = e.data;

      if (type === 'REGION_LOADED') {
        const features = fileData?.features || [];
        features.forEach(f => {
          const fid = f.properties?.id;
          if (fid !== undefined && fid !== null) featuresById.current.set(String(fid), f);
        });
        setRegionGeoData(prev => [...(prev || []), { key: file, features }]);
      } else if (type === 'PROGRESS') {
        setLoadProgress(progress);
      } else if (type === 'FILE_ERROR') {
        console.warn(`Data file failed: ${file} — ${message}`);
      } else if (type === 'DONE') {
        setDataLoaded(true);
        if (failed > 0) console.warn(`${failed} data file(s) could not be loaded`);
        worker.terminate();
      }
    };

    worker.postMessage({ type: 'START_FETCH', baseUrl: process.env.PUBLIC_URL || '' });

    return () => worker.terminate();
  }, []);

  useEffect(() => {
    const onResize = () => setViewState((v) => ({ ...v }));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // "Alan adı (ÜLKE)" etiketi — ülke adı yerine bölge kısaltması gösterilir
  const formatAreaLabel = useCallback((area) => {
    if (!area?.country) return area?.name ?? '';
    return `${area.name} (${t(`countries.${area.country}`, { defaultValue: area.country })})`;
  }, [t]);

  // Alan listesi (sadece aktif olanlar)
  const areas = useMemo(() => {
    if (!data) return [];
    return data.features
      .filter((f) => activeAreaIds.includes(String(f.properties?.id)))
      .map(toAreaEntry);
  }, [data, activeAreaIds]);

  // Potansiyel alan listesi (aktif olmayanlar)
  const potentialAreas = useMemo(() => {
    if (!data) return [];
    return data.features
      .filter((f) => !activeAreaIds.includes(String(f.properties?.id)))
      .map(toAreaEntry);
  }, [data, activeAreaIds]);

  // Seçili feature
  const selectedFeature = useMemo(() => {
    if (!data || !selectedFeatureId) return null;
    return data.features.find((f) => String(f.properties?.id) === String(selectedFeatureId)) || null;
  }, [data, selectedFeatureId]);

  // Alan geçişinde filtreleri kaydet / yükle + geometri on-demand yükle
  // inheritFilters: transplantasyonda kullanıcının o an ayarladığı etki değerleri
  // yeni alana taşınır (varsayılanlara dönülmez).
  const handleSelectFeature = useCallback((id, { inheritFilters = false } = {}) => {
    // Mevcut filtreleri eski alan için kaydet
    if (selectedFeatureId) {
      filtersPerArea.current[selectedFeatureId] = filtersRef.current;
    }

    // Yeni alanın filtreleri: transplantasyon → devralınan değerler,
    // daha önce ziyaret edilmiş alan → kayıtlı değerler, aksi halde → varsayılanlar
    let newFilters;
    if (!id) {
      newFilters = { ...DEFAULTS };
    } else if (inheritFilters) {
      newFilters = { ...filtersRef.current };
    } else {
      newFilters = filtersPerArea.current[id] ?? { ...DEFAULTS };
    }
    if (id) filtersPerArea.current[id] = newFilters;

    filtersRef.current = newFilters;
    setFilters(newFilters);
    // Alan değişiminde hemen uygula
    setAppliedFilters(newFilters);
    setSelectedFeatureId(id);
    setShowFilterPopup(false);
    setSelectedFeatureGeo(null); // Önceki geometriyi temizle

    if (id) {
      setFilterModalOpen(true);
      // Geometri zaten yüklü bölge verisinden gelir; ek bir ağ isteği yok.
      // Dosya henüz inmemişse aşağıdaki effect gelince tamamlar.
      const f = featuresById.current.get(String(id)) || null;
      setSelectedFeatureGeo(f);
      setGeoLoading(!f);
    } else {
      setGeoLoading(false);
    }
  }, [selectedFeatureId]);

  // Seçim, ilgili bölge dosyası henüz inmemişken yapıldıysa geldiğinde tamamla
  useEffect(() => {
    if (!selectedFeatureId || selectedFeatureGeo) return;
    const f = featuresById.current.get(String(selectedFeatureId));
    if (f) {
      setSelectedFeatureGeo(f);
      setGeoLoading(false);
    } else if (dataLoaded) {
      // Tüm dosyalar indi ama bu alan yok — beklemeyi bırak
      setGeoLoading(false);
    }
  }, [regionGeoData, dataLoaded, selectedFeatureId, selectedFeatureGeo]);

  // Seçili alanın hassas su maskesini yükle.
  // Maske inerken tahmin hesaplanmaz: kaba bir maskeyle geçici olarak yanlış
  // bir şekil çizip sonra yerine oturtmak yanıltıcı olurdu.
  useEffect(() => {
    if (!selectedFeatureId) { setSelectedMask(null); setMaskLoading(false); return; }
    const id = String(selectedFeatureId);

    if (masksById.current.has(id)) {
      setSelectedMask(masksById.current.get(id));
      setMaskLoading(false);
      return;
    }
    if (!selectedFeature?.properties?._hasMask) {
      setSelectedMask(null);
      setMaskLoading(false);
      return;
    }

    let cancelled = false;
    setSelectedMask(null);
    setMaskLoading(true);
    fetch(`${process.env.PUBLIC_URL}/masks/${encodeURIComponent(id)}.json`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(mask => {
        masksById.current.set(id, mask);
        if (!cancelled) setSelectedMask(mask);
      })
      .catch(e => {
        masksById.current.set(id, null); // tekrar denemeye gerek yok
        console.warn(`Local mask unavailable for ${id}:`, e.message);
      })
      .finally(() => { if (!cancelled) setMaskLoading(false); });
    return () => { cancelled = true; };
  }, [selectedFeatureId, selectedFeature]);

  // Seçilen alana zoom — metadata'daki bbox kullanarak anlık zoom (geometri beklenmez)
  useEffect(() => {
    if (!selectedFeature) return;

    // Pre-computed bbox veya constraints poligonundan bounds hesapla
    let boundsArray = selectedFeature.properties?._bbox;
    const constraintsPoly = selectedFeature.properties?.constraints?.maxExpansionPolygon;
    if (constraintsPoly) {
      try {
        boundsArray = turf.bbox(turf.feature(constraintsPoly));
      } catch { }
    }
    if (!boundsArray || !isFinite(boundsArray[0])) return;

    const [minLng, minLat, maxLng, maxLat] = boundsArray;

    const width = window.innerWidth;
    const height = window.innerHeight;

    const viewport = new WebMercatorViewport({
      width,
      height,
      longitude: viewState.longitude,
      latitude: viewState.latitude,
      zoom: viewState.zoom,
      pitch: viewState.pitch,
      bearing: viewState.bearing,
    }).fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 300 }
    );

    setViewState((v) => ({
      ...v,
      longitude: viewport.longitude,
      latitude: viewport.latitude,
      zoom: viewport.zoom,
      transitionDuration: 800,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  // Pre-computed centroid from metadata (no turf.js calculation needed)
  const selectedCentroid = useMemo(() => {
    if (!selectedFeature) return null;
    return selectedFeature.properties?._centroid || null;
  }, [selectedFeature]);

  // Prediction: on-demand yüklenen tam geometri (selectedFeatureGeo) kullanır
  const predictedFeature = useMemo(() => {
    if (!selectedFeatureGeo) return null;
    // Maske inene kadar bekle — yanlış kırpılmış geçici bir şekil çizilmesin
    if (maskLoading) return null;

    const rawScale = computeScale({
      temperature: appliedFilters.temperature,
      pollution: appliedFilters.pollution,
      salinity: appliedFilters.salinity,
      sulfur: appliedFilters.sulfur,
      phosphate: appliedFilters.phosphate,
      nitrogen: appliedFilters.nitrogen,
      chlorophyll: appliedFilters.chlorophyll,
      secchi: appliedFilters.secchi,
      hypoxia: appliedFilters.hypoxia,
      silica: appliedFilters.silica,
      regionPop: appliedFilters.regionPop,
      industry: appliedFilters.industry,
      shipWaste: appliedFilters.shipWaste,
      agriculture: appliedFilters.agriculture,
      marina: appliedFilters.marina,
      coastalBuild: appliedFilters.coastalBuild,
      wastewater: appliedFilters.wastewater,
      fishing: appliedFilters.fishing,
      aquaculture: appliedFilters.aquaculture,
      boat: appliedFilters.boat,
      shoaling: appliedFilters.shoaling,
      sedimentation: appliedFilters.sedimentation,
      stormImpact: appliedFilters.stormImpact,
      bathymetry: appliedFilters.bathymetry,
    });

    const yearFactor = horizonToFactor(horizon);
    const scale = rawScale * yearFactor;
    const scaled = scaleGeometry(selectedFeatureGeo, scale);
    if (!scaled) return null;

    // Apply constraints (intersection with maxExpansionPolygon)
    let constrained = applyConstraints(selectedFeatureGeo, scaled);

    // Tahmini geometriyi suyla kırp ki büyüme karaya taşmasın.
    //
    // Alana özel maske metre altı hassasiyettedir. Global sea_mask ise kıyıyı
    // 150–300 m açığa iten kaba bir maskedir: kıyıya yakın küçük yataklarda ya
    // hiç kesişmez (kırpma sessizce atlanır) ya da alanın tamamını siler. Bu
    // yüzden yalnızca hassas maskesi TANIMLI OLMAYAN alanlar için kullanılır;
    // maskesi olup da inemeyen alanda kırpma yapılmaz (yanlış şekil çizmektense
    // kırpmamak yeğdir) ve durum konsola yazılır.
    // Tahmini geometriyi suyla kırp ki büyüme karaya taşmasın.
    //
    // Yalnızca alana özel, DOĞRULANMIŞ maske kullanılır (bkz. scripts/prepare_data.js:
    // yatağının %60'ından azını su sayan maskeler elenir). Maskesi olmayan alanlarda
    // kırpma yapılmaz; büyüme zaten maxExpansionPolygon ile sınırlıdır. Global
    // sea_mask kıyıyı 150–300 m açığa iten kaba bir maskedir ve kıyıya yakın küçük
    // yataklarda ya hiç kesişmez ya da alanın tamamını siler — bu yüzden kullanılmaz.
    const areaId = String(selectedFeatureGeo.properties?.id);
    if (selectedMask) {
      try {
        const clipped = turf.intersect(
          turf.featureCollection([turf.feature(constrained), turf.feature(selectedMask)])
        );
        if (clipped) {
          constrained = clipped.geometry;
        } else {
          console.warn(`Water mask does not cover ${areaId} — prediction left unclipped`);
        }
      } catch (e) {
        console.warn("clip predicted error", e);
      }
    }

    // Büyüme yataktan kopamaz — kırpma sonrası ada parçaları atılır
    constrained = keepAttachedParts(constrained, selectedFeatureGeo, areaId);

    return {
      type: "Feature",
      properties: { ...selectedFeatureGeo.properties, predicted: true, scale },
      geometry: constrained,
    };
  }, [selectedFeatureGeo, appliedFilters, horizon, selectedMask, maskLoading]);

  // Büyüme halkası: predicted − original (mavi çekirdek korunur, kazanılan kısım yeşil).
  // Daralmada halka hesaplanmaz; küçülen tahmin alanı doğrudan kırmızı çizilir.
  const growthRing = useMemo(() => {
    if (!selectedFeatureGeo || !predictedFeature) return null;
    const scale = predictedFeature.properties?.scale ?? 1;
    if (scale <= 1) return null;
    try {
      const g = turf.difference(
        turf.featureCollection([predictedFeature, selectedFeatureGeo])
      );
      return g || null;
    } catch (e) {
      console.warn("growthRing diff error", e);
      return null;
    }
  }, [selectedFeatureGeo, predictedFeature]);

  // Pre-computed centroids from metadata (turf.js hesaplaması gerekmez)
  const allAreaCentroids = useMemo(() => {
    if (!data) return [];
    return data.features
      .filter(f => activeAreaIds.includes(String(f.properties?.id)))
      .map(f => {
        const centroid = f.properties?._centroid;
        if (!centroid) return null;
        return {
          position: centroid,
          id: String(f.properties?.id),
          name: f.properties?.name || String(f.properties?.id),
        };
      })
      .filter(Boolean);
  }, [data, activeAreaIds]);

  const layers = useMemo(() => {
    const out = [];

    // ── Bölge poligonları: uygulamanın tek veri kaynağı (public/geojson) ──
    // Dosya başına bir katman: yeni dosya geldiğinde deck.gl yalnızca onu üçgenler,
    // önceden yüklenmiş geometriler dokunulmadan kalır.
    const onFeatureClick = (info) => {
      const id = info?.object?.properties?.id;
      if (id !== undefined && id !== null) handleSelectFeature(String(id));
    };

    (regionGeoData || []).forEach(({ key, features }) => {
      out.push(
        new GeoJsonLayer({
          id: `seagrass_region_${key}`,
          data: features,
          stroked: true,
          filled: true,
          lineWidthMinPixels: 1.5,
          getFillColor: [59, 130, 246, 170],
          getLineColor: [30, 64, 175, 230],
          getLineWidth: 1.4,
          pickable: true,
          onClick: onFeatureClick,
        })
      );
    });

    // ── Original (current) form overlay — eski hali tahminin altında görünür kalsın ──
    if (selectedFeatureGeo) {
      out.push(
        new GeoJsonLayer({
          id: "seagrass_original",
          data: selectedFeatureGeo,
          stroked: true,
          filled: true,
          lineWidthMinPixels: 1.5,
          getFillColor: [59, 130, 246, 180],   // mavi – mevcut alan dolu kalsın
          getLineColor: [30, 64, 175, 220],
          getLineWidth: 1.4,
          parameters: { depthTest: false },
        })
      );
    }

    // ── Değişim bölgesi: mevcut (mavi) alan dolu kalır ──
    if (predictedFeature) {
      const scale = predictedFeature.properties?.scale ?? 1;
      if (scale > 1.0001) {
        // Büyüme → kazanılan kısım yeşil halka (mavi çekirdek korunur).
        // Halka hesaplanamazsa tam tahmin (fallback).
        out.push(
          new GeoJsonLayer({
            id: "seagrass_growth",
            data: growthRing || predictedFeature,
            stroked: true,
            filled: true,
            lineWidthMinPixels: 2,
            getFillColor: [34, 197, 94, 160],   // yeşil – büyüme
            getLineColor: [22, 163, 74, 240],
            parameters: { depthTest: false },
          })
        );
      } else if (scale < 0.9999) {
        // Daralma → küçülen (kalan) alan kırmızı; kaybedilen kısım altta mavi kalır.
        out.push(
          new GeoJsonLayer({
            id: "seagrass_shrink",
            data: predictedFeature,
            stroked: true,
            filled: true,
            lineWidthMinPixels: 2,
            getFillColor: [239, 68, 68, 200],   // kırmızı – küçülen alan
            getLineColor: [220, 38, 38, 240],
            parameters: { depthTest: false },
          })
        );
      }
      // scale ≈ 1 → değişim yok, yalnızca mavi mevcut alan görünür
    }

    // ── Original boundary outline (en üstte) — eski form sınırı asla kaybolmasın ──
    if (selectedFeatureGeo) {
      out.push(
        new GeoJsonLayer({
          id: "seagrass_original_outline",
          data: selectedFeatureGeo,
          stroked: true,
          filled: false,
          lineWidthMinPixels: 2,
          getLineColor: [37, 99, 235, 255],
          getLineWidth: 2,
          parameters: { depthTest: false },
        })
      );
    }

    // Markers for ALL active areas (excluding selected one to avoid overlap)
    if (allAreaCentroids.length > 0) {
      const nonSelectedCentroids = allAreaCentroids.filter(c => c.id !== selectedFeatureId);
      if (nonSelectedCentroids.length > 0) {
        out.push(
          new TextLayer({
            id: "area_markers",
            data: nonSelectedCentroids,
            getPosition: (d) => d.position,
            getText: (d) => `🌿 ${d.name}`,
            getSize: 14,
            getColor: [255, 255, 255, 230],
            outlineWidth: 3,
            outlineColor: [0, 0, 0, 200],
            pixelOffset: [0, -8],
            getTextAnchor: "middle",
            getAlignmentBaseline: "center",
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            fontWeight: 600,
            characterSet: "abcçdefgğhıijklmnoöpqrsştuüvwxyzABCÇDEFGĞHIİJKLMNOÖPQRSŞTUÜVWXYZ0123456789.,?!%+-_()[]/&' \n🌿📍",
            parameters: { depthTest: false },
            pickable: true,
            onClick: (info) => {
              const id = info?.object?.id;
              if (id) handleSelectFeature(id);
            },
          })
        );
      }
    }
    if (selectedCentroid) {
      const scaleVal = predictedFeature?.properties?.scale;
      const scaleText = scaleVal != null && scaleVal !== 1
        ? `\n${scaleVal >= 1 ? '+' : ''}${((scaleVal - 1) * 100).toFixed(0)}%`
        : '';
      out.push(
        new TextLayer({
          id: "location_label",
          data: [{ position: selectedCentroid, name: selectedFeature?.properties?.name + scaleText }],
          getPosition: (d) => d.position,
          getText: (d) => d.name,
          getSize: 18,
          getColor: [255, 255, 255],
          outlineWidth: 3,
          outlineColor: [0, 0, 0, 180],
          pixelOffset: [0, 0],
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
          fontWeight: 700,
          characterSet: "abcçdefgğhıijklmnoöpqrsştuüvwxyzABCÇDEFGĞHIİJKLMNOÖPQRSŞTUÜVWXYZ0123456789.,?!%+-_()[]/&' \n",
          parameters: { depthTest: false },
        })
      );
    }
    return out;
  }, [regionGeoData, selectedFeatureGeo, predictedFeature, growthRing, selectedCentroid, selectedFeature, handleSelectFeature, allAreaCentroids, selectedFeatureId]);

  // Check if draft filters differ from applied filters
  const hasPendingFilters = useMemo(() => {
    return Object.keys(DEFAULTS).some(k => filters[k] !== appliedFilters[k]);
  }, [filters, appliedFilters]);

  const handleResetFilters = useCallback(() => {
    setFilters({ ...DEFAULTS });
    setAppliedFilters({ ...DEFAULTS });
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleTransplant = useCallback((id) => {
    if (!id) return;
    setActiveAreaIds((prev) => Array.from(new Set([...prev, id])));
    setTransplantedAreaIds((prev) => Array.from(new Set([...prev, id])));
    setTransplantModalOpen(false);
    // Kullanıcının seçtiği etki değerleri hedef alana taşınır
    handleSelectFeature(id, { inheritFilters: true });
  }, [handleSelectFeature]);

  const handleCancelTransplant = useCallback(() => {
    if (!selectedFeatureId || !transplantedAreaIds.includes(selectedFeatureId)) return;
    const cancelledId = selectedFeatureId;
    setTransplantedAreaIds((prev) => prev.filter(id => id !== cancelledId));
    setActiveAreaIds((prev) => prev.filter(id => id !== cancelledId));
    handleSelectFeature(null);
    // İptal edilen transplantasyonun devralınmış filtreleri hafızada kalmasın
    delete filtersPerArea.current[cancelledId];
  }, [selectedFeatureId, transplantedAreaIds, handleSelectFeature]);

  return (
    <div style={{ height: "100vh" }}>
      {/* ── Transplantation Alert ── */}
      {selectedFeatureId && transplantedAreaIds.includes(selectedFeatureId) && (
        <div className="transplant-alert">
          <div className="transplant-alert__content">
            <span className="transplant-alert__icon">⚠️</span>
            {t('ui.transplantNotice')}
          </div>
        </div>
      )}

      {/* ── Satellite Data Alert ── */}
      {selectedFeature?.properties?.IsSatelliteData && (
        <div className="satellite-warning">
          <div className="satellite-warning__content">
            <span className="satellite-warning__icon">🛰️</span>
            {t('ui.satelliteWarning')}
          </div>
        </div>
      )}

      {/* ── Açılış splash ekranı ── */}
      {loadingState === 'init' && <LoadingScreen t={t} progress={loadProgress} />}

      {/* ── Harita verisi arka planda yüklenirken engellemeyen gösterge ── */}
      {loadingState === 'ready' && !dataLoaded && (
        <div className="data-loading-indicator">
          <div className="data-loading-indicator__label">
            {t('ui.loadingSubtitle')} %{loadProgress}
          </div>
          <div className="data-loading-indicator__bar">
            <div
              className="data-loading-indicator__bar-fill"
              style={{ width: `${loadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Sidebar (compact) ── */}
      <Sidebar
        areas={areas}
        formatAreaLabel={formatAreaLabel}
        selectedFeatureId={selectedFeatureId}
        handleSelectFeature={handleSelectFeature}
        predictedFeature={predictedFeature}
        onOpenFilters={() => setFilterModalOpen(true)}
        onResetFilters={handleResetFilters}
        onTransplant={() => setTransplantModalOpen(true)}
        onCancelTransplant={handleCancelTransplant}
        isTransplanted={transplantedAreaIds.includes(selectedFeatureId)}
        t={t}
        i18n={i18n}
      />

      {/* ── Filter Panel (sağ panel, haritayı blurlamaz) ── */}
      {filterModalOpen && (
        <FilterPanel
          filters={filters}
          updateFilter={updateFilter}
          onClose={() => setFilterModalOpen(false)}
          onReset={handleResetFilters}
          selectedAreaName={selectedFeature?.properties?.name}
          geoLoading={geoLoading}
          horizon={horizon}
          setHorizon={(h) => {
            setHorizon(h);
            setAppliedFilters({ ...filters });
          }}
          hasPendingFilters={hasPendingFilters}
          scale={predictedFeature?.properties?.scale}
          t={t}
        />
      )}

      {/* ── Filter Info Popup (on marker click) ── */}
      {showFilterPopup && selectedFeature && (
        <FilterInfoPopup
          filters={filters}
          featureName={selectedFeature.properties?.name || selectedFeatureId}
          scale={predictedFeature?.properties?.scale}
          horizon={horizon}
          onClose={() => setShowFilterPopup(false)}
          t={t}
        />
      )}


      {/* ── Transplant Modal ── */}
      {transplantModalOpen && (
        <TransplantModal
          potentialAreas={potentialAreas}
          formatAreaLabel={formatAreaLabel}
          onTransplant={handleTransplant}
          onClose={() => setTransplantModalOpen(false)}
          t={t}
        />
      )}

      <DeckGL
        viewState={viewState}
        controller={true}
        layers={layers}
        onViewStateChange={({ viewState: vs }) => {
          vs.longitude = Math.max(MAP_BOUNDS.minLng, Math.min(MAP_BOUNDS.maxLng, vs.longitude));
          vs.latitude = Math.max(MAP_BOUNDS.minLat, Math.min(MAP_BOUNDS.maxLat, vs.latitude));
          vs.zoom = Math.max(MAP_BOUNDS.minZoom, Math.min(MAP_BOUNDS.maxZoom, vs.zoom));
          setViewState(vs);
        }}
      >
        <MapLibreMap
          mapStyle={{
            version: 8,
            sources: {
              satellite: {
                type: "raster",
                tiles: [
                  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
                ],
                tileSize: 256,
              },
              labels: {
                type: "raster",
                tiles: [
                  "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
                ],
                tileSize: 256,
              },
            },
            layers: [
              {
                id: "satellite",
                type: "raster",
                source: "satellite",
              },
              {
                id: "labels",
                type: "raster",
                source: "labels",
              },
            ],
          }}
          style={{ width: "100%", height: "100%" }}
        />
      </DeckGL>


      {/* ── Brand Logo (Bottom Left) ── */}
      <div className="brand-logo">
        <img
          src={process.env.PUBLIC_URL + "/bluec_logo.png"}
          alt="Interreg NEXT Black Sea Basin — Co-funded by the European Union"
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*                  LOADING SCREEN (splash)                      */
/* ══════════════════════════════════════════════════════════════ */
function LoadingScreen({ t, progress }) {
  return (
    <div className="loading-screen">
      <div className="loading-screen__content">
        <div className="loading-screen__icon">
          <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="72" height="72">
            <ellipse cx="32" cy="52" rx="20" ry="6" fill="#0d9488" opacity="0.3" />
            <path d="M12 40 Q20 28 32 32 Q44 36 52 24" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" fill="none" />
            <path d="M16 46 Q26 34 36 38 Q46 42 54 30" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            <path d="M20 34 Q28 22 40 26" stroke="#6ee7b7" strokeWidth="2" strokeLinecap="round" fill="none" />
            <circle cx="32" cy="18" r="6" fill="#10b981" opacity="0.7" />
            <path d="M32 18 L32 8" stroke="#10b981" strokeWidth="2" strokeLinecap="round" />
            <path d="M28 12 L32 8 L36 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h2 className="loading-screen__title">{t('ui.loadingTitle')}</h2>
        <p className="loading-screen__subtitle">{t('ui.loadingSubtitle')}</p>
        <div className="loading-screen__bar">
          <div
            className="loading-screen__bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="loading-screen__percentage">%{progress}</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*                     SIDEBAR (Compact)                         */
/* ══════════════════════════════════════════════════════════════ */
function Sidebar({
  areas,
  formatAreaLabel,
  selectedFeatureId,
  handleSelectFeature,
  predictedFeature,
  onOpenFilters,
  onResetFilters,
  onTransplant,
  onCancelTransplant,
  isTransplanted,
  t,
  i18n
}) {
  return (
    <div className="sidebar">
      {/* Başlık satırı: solda bölüm adı, sağda dil seçici — select tam genişlik kalsın */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <div className="section-title" style={{ marginBottom: 0 }}>{t('ui.areaSelection')}</div>
        <div style={{ width: '80px', flex: '0 0 auto' }} title={t('ui.language')}>
          <LanguageSwitcher i18n={i18n} />
        </div>
      </div>
      <select
        value={selectedFeatureId || ""}
        onChange={(e) => handleSelectFeature(e.target.value || null)}
        style={{ marginBottom: '8px' }}
      >
        <option value="">{t('ui.selectArea')}</option>
        {areas.map((a) => (
          <option key={a.id} value={a.id}>
            {formatAreaLabel(a)}
          </option>
        ))}
      </select>

      {selectedFeatureId && (
        <>
          <button className="btn btn-transplant" onClick={onTransplant}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('ui.transplant')}
          </button>
          {isTransplanted && (
            <button className="btn btn-cancel-transplant" onClick={onCancelTransplant}
              style={{ background: '#ef4444', marginTop: '4px' }}>
              ✕ {t('ui.cancelTransplant')}
            </button>
          )}
        </>
      )}

      {predictedFeature && (
        <div className="scale-info">
          {t('ui.predictedScale')} <b>{predictedFeature.properties.scale.toFixed(2)}x</b>
          <div className="legend-row">
            <span className="legend-dot" style={{ background: "#3B82F6" }} />
            {t('ui.currentArea')}
          </div>
          <div className="legend-row">
            <span className="legend-dot" style={{ background: "#22C55E" }} />
            {t('ui.growth')}
          </div>
          <div className="legend-row">
            <span className="legend-dot" style={{ background: "#EF4444" }} />
            {t('ui.shrinkage')}
          </div>
        </div>
      )}

      <button className="btn btn-reset" onClick={onResetFilters}>
        {t('ui.resetFilters')}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*                     FILTER MODAL                              */
/* ══════════════════════════════════════════════════════════════ */
function FilterPanel({ filters, updateFilter, onClose, onReset, selectedAreaName, horizon, setHorizon, hasPendingFilters, geoLoading, scale, t }) {
  const [activeTab, setActiveTab] = useState("chemical");

  const filterTabs = [
    { id: "chemical", label: t('ui.chemical') },
    { id: "human", label: t('ui.human') },
    { id: "environmental", label: t('ui.environmental') },
  ];

  const horizonTabs = [
    { value: "N", label: t('ui.today') },
    { value: "1Y", label: t('ui.year1') },
    { value: "5Y", label: t('ui.year5') },
    { value: "10Y", label: t('ui.year10') },
  ];

  return (
    <div className="filter-panel">
      <div className="filter-panel__header">
        <h3>{t('ui.filterParams')} {selectedAreaName && <span style={{ opacity: 0.8, fontSize: '0.9em', fontWeight: 400 }}>— {selectedAreaName}</span>}{geoLoading && <span style={{ marginLeft: 8, fontSize: '0.8em', opacity: 0.7 }}>⏳</span>}</h3>
        <div className="filter-panel__header-actions">
          <button className="btn-icon" onClick={onReset} title={t('ui.reset')}>↺</button>
          <button className="btn-icon btn-icon--close" onClick={onClose} title={t('ui.close')}>×</button>
        </div>
      </div>

      {/* ── Time Period Selector ── */}
      <div className="filter-panel__horizon">
        {hasPendingFilters && (
          <span className="horizon-pending-dot" title={t('ui.pendingFilters') || 'Değişiklikleri uygulamak için bir zaman periyoduna tıklayın'}>●</span>
        )}
        {horizonTabs.map((tab) => (
          <button
            key={tab.value}
            className={`filter-panel__horizon-tab${horizon === tab.value ? ' active' : ''}${hasPendingFilters ? ' pending' : ''}`}
            onClick={() => setHorizon(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="filter-panel__tabs">
        {filterTabs.map((tab) => (
          <button
            key={tab.id}
            className={`filter-panel__tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="filter-panel__body">
        {activeTab === "chemical" && (
          <ChemicalFilters filters={filters} updateFilter={updateFilter} t={t} />
        )}
        {activeTab === "human" && (
          <HumanFilters filters={filters} updateFilter={updateFilter} t={t} />
        )}
        {activeTab === "environmental" && (
          <EnvironmentalFilters filters={filters} updateFilter={updateFilter} t={t} />
        )}
      </div>

      {Number.isFinite(scale) && (() => {
        const out = computeOutputs(scale);
        return (
          <div className="filter-panel__outputs">
            <OutputRow label={t('outputs.percentageCover')}   data={out.cover}    decimals={1} />
            <OutputRow label={t('outputs.shootCount')}        data={out.shoot}    decimals={0} />
            <OutputRow label={t('outputs.horizontalRhizome')} data={out.rhizome}  decimals={0} />
            <OutputRow label={t('outputs.apicalMeristem')}    data={out.meristem} decimals={0} />
          </div>
        );
      })()}
    </div>
  );
}

function OutputRow({ label, data, decimals }) {
  const sign = data.delta > 0 ? "+" : "";
  const color = Math.abs(data.delta) < 0.05 ? "#888"
              : data.delta > 0 ? "#10b981" : "#ef4444";
  return (
    <div className="output-row">
      <div className="output-row__label">{label}</div>
      <div className="output-row__values">
        <span className="output-row__delta" style={{ color }}>
          {sign}{data.delta.toFixed(1)}%
        </span>
        <span className="output-row__arrow">→</span>
        <span className="output-row__abs">
          {data.abs.toFixed(decimals)} {data.unit}
        </span>
      </div>
    </div>
  );
}


/* ── Reusable slider row ── */
function SliderRow({
  label,
  filterKey,
  value,
  min,
  max,
  step,
  defaultVal,
  isPositive,
  updateFilter,
}) {
  const color = getSliderColor(value, min, max, defaultVal, isPositive);
  const changed = value !== defaultVal;
  const pctChange = defaultVal !== 0
    ? ((value - defaultVal) / Math.abs(defaultVal) * 100).toFixed(1)
    : null;
  return (
    <div className="filter-row">
      <div className="filter-head">
        <label>
          {label}
          {changed && (
            <span className="filter-default-hint">
              {" "}(⇦ {defaultVal}{pctChange != null ? `, ${pctChange > 0 ? '+' : ''}${pctChange}%` : ''})
            </span>
          )}
        </label>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) =>
            updateFilter(
              filterKey,
              Math.max(min, Math.min(max, Number(e.target.value)))
            )
          }
        />
      </div>
      <div className="slider-wrap">
        <span>{min}</span>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          style={{ color, accentColor: color }}
          onChange={(e) => updateFilter(filterKey, Number(e.target.value))}
        />
        <span>{max}</span>
      </div>
    </div>
  );
}

/* ── Chemical Filters ── */
function ChemicalFilters({ filters, updateFilter, t }) {
  return (
    <>
      <SliderRow label={t("filters.sulfur")} filterKey="sulfur" value={filters.sulfur} min={0} max={1} step={0.01} defaultVal={DEFAULTS.sulfur} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.phosphate")} filterKey="phosphate" value={filters.phosphate} min={0} max={1} step={0.01} defaultVal={DEFAULTS.phosphate} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.nitrogen")} filterKey="nitrogen" value={filters.nitrogen} min={0} max={2} step={0.01} defaultVal={DEFAULTS.nitrogen} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.chlorophyll")} filterKey="chlorophyll" value={filters.chlorophyll} min={0} max={5} step={0.05} defaultVal={DEFAULTS.chlorophyll} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.secchi")} filterKey="secchi" value={filters.secchi} min={0} max={25} step={0.5} defaultVal={DEFAULTS.secchi} isPositive={true} updateFilter={updateFilter} />
      <SliderRow label={t("filters.hypoxia")} filterKey="hypoxia" value={filters.hypoxia} min={0} max={1} step={0.01} defaultVal={DEFAULTS.hypoxia} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.silica")} filterKey="silica" value={filters.silica} min={0} max={5} step={0.01} defaultVal={DEFAULTS.silica} isPositive={true} updateFilter={updateFilter} />
      <SliderRow label={t("filters.pollution")} filterKey="pollution" value={filters.pollution} min={0} max={1} step={0.01} defaultVal={DEFAULTS.pollution} isPositive={false} updateFilter={updateFilter} />
    </>
  );
}

/* ── Human Filters ── */
function HumanFilters({ filters, updateFilter, t }) {
  return (
    <>
      <SliderRow label={t("filters.regionPop")} filterKey="regionPop" value={filters.regionPop} min={0} max={1} step={0.01} defaultVal={DEFAULTS.regionPop} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.industry")} filterKey="industry" value={filters.industry} min={0} max={1} step={0.01} defaultVal={DEFAULTS.industry} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.shipWaste")} filterKey="shipWaste" value={filters.shipWaste} min={0} max={1} step={0.01} defaultVal={DEFAULTS.shipWaste} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.agriculture")} filterKey="agriculture" value={filters.agriculture} min={0} max={1} step={0.01} defaultVal={DEFAULTS.agriculture} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.marina")} filterKey="marina" value={filters.marina} min={0} max={1} step={0.01} defaultVal={DEFAULTS.marina} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.coastalBuild")} filterKey="coastalBuild" value={filters.coastalBuild} min={0} max={1} step={0.01} defaultVal={DEFAULTS.coastalBuild} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.wastewater")} filterKey="wastewater" value={filters.wastewater} min={0} max={1} step={0.01} defaultVal={DEFAULTS.wastewater} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.fishing")} filterKey="fishing" value={filters.fishing} min={0} max={1} step={0.01} defaultVal={DEFAULTS.fishing} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.aquaculture")} filterKey="aquaculture" value={filters.aquaculture} min={0} max={1} step={0.01} defaultVal={DEFAULTS.aquaculture} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.boat")} filterKey="boat" value={filters.boat} min={0} max={1} step={0.01} defaultVal={DEFAULTS.boat} isPositive={false} updateFilter={updateFilter} />
    </>
  );
}

/* ── Environmental Filters ── */
function EnvironmentalFilters({ filters, updateFilter, t }) {
  return (
    <>
      <SliderRow label={t("filters.shoaling")} filterKey="shoaling" value={filters.shoaling} min={0} max={1} step={0.01} defaultVal={DEFAULTS.shoaling} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.sedimentation")} filterKey="sedimentation" value={filters.sedimentation} min={0} max={1} step={0.01} defaultVal={DEFAULTS.sedimentation} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.stormImpact")} filterKey="stormImpact" value={filters.stormImpact} min={0} max={1} step={0.01} defaultVal={DEFAULTS.stormImpact} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.temperature")} filterKey="temperature" value={filters.temperature} min={10} max={35} step={0.5} defaultVal={DEFAULTS.temperature} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.salinity")} filterKey="salinity" value={filters.salinity} min={10} max={25} step={0.5} defaultVal={DEFAULTS.salinity} isPositive={false} updateFilter={updateFilter} />
      <SliderRow label={t("filters.bathymetry")} filterKey="bathymetry" value={filters.bathymetry} min={0} max={40} step={0.5} defaultVal={DEFAULTS.bathymetry} isPositive={true} updateFilter={updateFilter} />
    </>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*               FILTER INFO POPUP (on marker click)             */
/* ══════════════════════════════════════════════════════════════ */
function FilterInfoPopup({ filters, featureName, scale, horizon, onClose, t }) {
  const horizonLabel = { N: t('ui.today'), "1Y": t('ui.year1'), "5Y": t('ui.year5'), "10Y": t('ui.year10') }[horizon] || horizon;

  const chemKeys = ["sulfur", "phosphate", "nitrogen", "chlorophyll", "secchi", "hypoxia", "silica", "pollution"];
  const humanKeys = ["regionPop", "industry", "shipWaste", "agriculture", "marina", "coastalBuild", "wastewater", "fishing", "aquaculture", "boat"];
  const envKeys = ["shoaling", "sedimentation", "stormImpact", "temperature", "salinity", "bathymetry"];

  const renderGroup = (title, keys) => (
    <div className="fip-group">
      <div className="fip-group-title">{title}</div>
      {keys.map((k) => {
        const changed = filters[k] !== DEFAULTS[k];
        return (
          <div key={k} className={`fip-row${changed ? " changed" : ""}`}>
            <span className="fip-label">{t(`filters.${k}`)}</span>
            <span className="fip-value">{filters[k]}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="filter-info-popup">
      <div className="fip-header">
        <div>
          <strong>📍 {featureName}</strong>
          <span className="fip-meta"> · {horizonLabel}{scale != null ? ` · ${t('ui.scaleSuffix')}: ${scale.toFixed(2)}x` : ""}</span>
        </div>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div className="fip-body">
        {renderGroup(t('ui.chemical'), chemKeys)}
        {renderGroup(t('ui.human'), humanKeys)}
        {renderGroup(t('ui.climate'), envKeys)}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*                    LANGUAGE SWITCHER                          */
/* ══════════════════════════════════════════════════════════════ */
function LanguageSwitcher({ i18n }) {
  const currentLang = i18n.language.split("-")[0]; // tr-TR -> tr

  // List of available languages for future extensibility
  const languages = [
    { code: 'tr', label: 'TR' },
    { code: 'en', label: 'EN' }
  ];

  return (
    <div className="language-combobox">
      <select
        value={currentLang}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
      >
        {languages.map(lang => (
          <option key={lang.code} value={lang.code}>
            {lang.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*                    HELPER FUNCTIONS                           */
/* ══════════════════════════════════════════════════════════════ */

function horizonToFactor(h) {
  if (h === "N") return 1;
  if (h === "1Y") return 0.75;
  if (h === "5Y") return 0.5;
  if (h === "10Y") return 0.25;
  return 1.0;
}

const REF = {
  sulfur: 0.1,
  phosphate: 0.03,
  nitrogen: 0.44,
  chl: 0.71,
  secchi: 11,
  hypoxia: 0.05,
  silica: 2.76,
  pollution: 0.1,
  regionPop: 0.3,
  industry: 0.1,
  shipWaste: 0.1,
  agriculture: 0.1,
  marina: 0.1,
  coastalBuild: 0.1,
  wastewater: 0.1,
  fishing: 0.1,
  aquaculture: 0.1,
  boat: 0.1,
  shoaling: 0.1,
  sedimentation: 0.1,
  stormImpact: 0.1,
  bathy: 10,
  temp: 22.5,
  salinity: 18,
};

const w = {
  sulfur: 0.8,
  phosphate: 1.2,
  nitrogen: 1.0,
  chl: 0.5,
  secchi: 1.5,
  hypoxia: 1.4,
  silica: 0.3,
  pollution: 1.1,
  regionPop: 0.4,
  industry: 0.7,
  shipWaste: 0.6,
  agriculture: 0.8,
  marina: 0.5,
  coastalBuild: 0.7,
  wastewater: 1.0,
  fishing: 0.4,
  aquaculture: 0.5,
  boat: 0.3,
  shoaling: 0.6,
  sedimentation: 0.7,
  stormImpact: 0.8,
  bathy: 0.9,
  temp: 0.3,
  salinity: 0.4,
};

function computeScale(p) {
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

  let impact = 0;

  // Kimyasal (negatif: artarsa küçülür)
  impact -= w.sulfur * (p.sulfur - REF.sulfur);
  impact -= w.phosphate * (p.phosphate - REF.phosphate);
  impact -= w.nitrogen * (p.nitrogen - REF.nitrogen);
  impact -= w.chl * (p.chlorophyll - REF.chl);
  impact -= w.pollution * (p.pollution - REF.pollution);
  impact -= w.hypoxia * (p.hypoxia - REF.hypoxia);

  // Secchi (pozitif: artarsa büyür)
  impact += (w.secchi * (p.secchi - REF.secchi)) / 10;

  // Silis
  impact += w.silica * (p.silica - REF.silica);

  // İnsani (artarsa küçülür)
  impact -= w.regionPop * (p.regionPop - REF.regionPop);
  impact -= w.industry * (p.industry - REF.industry);
  impact -= w.shipWaste * (p.shipWaste - REF.shipWaste);
  impact -= w.agriculture * (p.agriculture - REF.agriculture);
  impact -= w.marina * (p.marina - REF.marina);
  impact -= w.coastalBuild * (p.coastalBuild - REF.coastalBuild);
  impact -= w.wastewater * (p.wastewater - REF.wastewater);
  impact -= w.fishing * (p.fishing - REF.fishing);
  impact -= w.aquaculture * (p.aquaculture - REF.aquaculture);
  impact -= w.boat * ((p.boat || 0) - REF.boat);

  // Çevresel
  impact -= w.shoaling * (p.shoaling - REF.shoaling);
  impact -= w.sedimentation * (p.sedimentation - REF.sedimentation);
  impact -= w.stormImpact * (p.stormImpact - REF.stormImpact);

  // Derinlik
  const bathyDiff = Math.abs(p.bathymetry - 7.5);
  const refBathyDiff = Math.abs(REF.bathy - 7.5);
  impact -= (w.bathy * (bathyDiff - refBathyDiff)) / 10;

  // Sıcaklık
  const tempDiff = Math.abs(p.temperature - 22.5);
  const refTempDiff = Math.abs(REF.temp - 22.5);
  impact -= (w.temp * (tempDiff - refTempDiff)) / 5;

  // Tuzluluk
  const salDiff = Math.abs(p.salinity - 18);
  const refSalDiff = Math.abs(REF.salinity - 18);
  impact -= (w.salinity * (salDiff - refSalDiff)) / 10;

  let scale = 1.0 + impact;
  scale = clamp(scale, 0.1, 4.0);
  return scale;
}

function scaleGeometry(featureOrGeom, scale) {
  const feature =
    featureOrGeom.type === "Feature"
      ? featureOrGeom
      : { type: "Feature", geometry: featureOrGeom, properties: {} };
  try {
    const scaled = turf.transformScale(feature, scale, { origin: "centroid" });
    return scaled.geometry;
  } catch (e) {
    console.warn("scaleGeometry error", e);
    return null;
  }
}

// Clip scaled geometry to maxExpansionPolygon to prevent land overflow
function applyConstraints(feature, scaledGeometry) {
  const constraints = feature.properties?.constraints;
  if (!constraints || !constraints.maxExpansionPolygon) {
    return scaledGeometry;
  }
  try {
    const base = turf.feature(scaledGeometry);
    const maxP = turf.feature(constraints.maxExpansionPolygon);
    const clipped = turf.intersect(turf.featureCollection([base, maxP]));
    return clipped ? clipped.geometry : scaledGeometry;
  } catch (e) {
    console.warn("applyConstraints error", e);
    return scaledGeometry;
  }
}

/**
 * Yataktan kopuk parçaları atar.
 *
 * Deniz çayırı rizomla yayılır: büyüme mevcut yataktan devam eder, karayı atlayıp
 * öbür tarafta yeniden başlayamaz. Ama tavan içbükey olduğunda (mendirek, liman
 * ağzı) ölçeklenen poligon karanın öbür yanında tavana yeniden giriyor ve kırpma,
 * yatağa hiç değmeyen ada parçaları üretiyor — Şile'de 1.56x'te 3 parça, 3x'te
 * tahminin %28'i kopuk. Yatağa değmeyen her parça atılır.
 *
 * Yatağın kendisi çok parçalıysa her parça kendi büyümesini taşır; kural
 * "mevcut yataktan devam et"tir, "tek parça ol" değil.
 */
function keepAttachedParts(geometry, bedFeature, areaId) {
  if (!geometry || geometry.type !== "MultiPolygon") return geometry;
  try {
    const bed = turf.feature(bedFeature.geometry || bedFeature);
    const attached = geometry.coordinates.filter((coords) => {
      try {
        return !!turf.intersect(turf.featureCollection([turf.polygon(coords), bed]));
      } catch {
        return true; // karar veremiyorsak parçayı atma
      }
    });
    // Hiçbiri değmiyorsa geometriyi boşaltmaktansa olduğu gibi bırak
    if (!attached.length || attached.length === geometry.coordinates.length) return geometry;
    console.warn(
      `${areaId}: yataktan kopuk ${geometry.coordinates.length - attached.length} parça atıldı`
    );
    return attached.length === 1
      ? { type: "Polygon", coordinates: attached[0] }
      : { type: "MultiPolygon", coordinates: attached };
  } catch (e) {
    console.warn("keepAttachedParts error", e);
    return geometry;
  }
}

/* ══════════════════════════════════════════════════════════════ */
/*                    TRANSPLANT MODAL                           */
/* ══════════════════════════════════════════════════════════════ */
function TransplantModal({ potentialAreas, formatAreaLabel, onTransplant, onClose, t }) {
  const [selectedId, setSelectedId] = useState(null);

  return (
    <div className="transplant-modal-overlay" onClick={onClose}>
      <div className="transplant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="transplant-modal__header">
          <h3>{t('ui.potentialAreas')}</h3>
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
        <div className="transplant-modal__body">
          <p style={{ color: '#64748b', marginBottom: '20px' }}>{t('ui.selectToTransplant')}</p>
          <div className="potential-area-list">
            {potentialAreas.map((area) => (
              <div
                key={area.id}
                className={`potential-area-item${selectedId === area.id ? ' selected' : ''}`}
                onClick={() => setSelectedId(area.id)}
              >
                <div className="potential-area-item__icon">
                  {selectedId === area.id ? '✓' : '📍'}
                </div>
                {formatAreaLabel(area)}
              </div>
            ))}
          </div>
        </div>
        <div className="transplant-modal__footer">
          <button className="btn" onClick={onClose}>{t('ui.cancel')}</button>
          <button
            className="btn btn-primary"
            disabled={!selectedId}
            onClick={() => onTransplant(selectedId)}
          >
            {t('ui.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
